<script setup lang="ts">
import { computed } from 'vue'

import type { AnalysisSchedule, ProfileConfigSnapshot } from '../../models/impact.types'
import { presentAnalysisScope } from '../../service/effectPresentation'

import { MetricLabel } from '@/features/reports'
import { useI18n } from '@/shared/i18n'
import { useFormat } from '@/shared/lib/format'
import { Icon, Popover } from '@/shared/ui'

const props = defineProps<{
  schedule: AnalysisSchedule
  snapshot: ProfileConfigSnapshot
}>()

const { t } = useI18n()
const format = useFormat()
const presentation = computed(() =>
  presentAnalysisScope({
    ...props.snapshot,
    max_credits: props.snapshot.max_credits ?? props.schedule.max_credits ?? null,
  }),
)
const period = computed(() => {
  const input = props.schedule.input_parameters
  if (input.intervention_date) return format.formatDate(input.intervention_date)
  if (!input.date_start || !input.date_end) return ''
  return `${format.formatDate(input.date_start)} – ${format.formatDate(input.date_end)}`
})
</script>

<template>
  <Popover
    :trigger-label="t('impactAnalysis.scope.trigger')"
    :panel-label="t('impactAnalysis.scope.title')"
    trigger-variant="plain"
    panel-variant="plain"
    placement="bottom-start"
    open-on-hover
  >
    <template #trigger>
      <span
        class="analysis-scope__trigger inline-flex items-center gap-1.5 ml-2 py-0.5 px-2 border border-border rounded-sm text-muted bg-surface text-[0.6875rem] font-semibold"
      >
        <Icon name="filter" :size="11" />
        {{ t('impactAnalysis.scope.trigger') }}
      </span>
    </template>

    <div
      class="analysis-scope flex w-[min(20rem,_calc(100vw_-_2rem))] flex-col gap-3 p-4 border border-border rounded-xl text-fg bg-surface shadow-pop"
      data-testid="impact-scope-popover"
    >
      <strong
        class="analysis-scope__eyebrow text-muted text-[0.5625rem] tracking-[0.12em] uppercase"
        >{{ t('impactAnalysis.scope.title') }}</strong
      >

      <div
        v-if="period"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.period') }}</span
        ><strong>{{ period }}</strong>
      </div>
      <div
        v-if="presentation.targetMetric"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.mainMetric') }}</span>
        <MetricLabel :field="presentation.targetMetric" :size="12" />
      </div>
      <div
        v-if="presentation.controlMetrics.length"
        class="analysis-scope__row is-top flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.controlMetrics') }}</span>
        <div class="analysis-scope__chips flex min-w-0 flex-wrap justify-end gap-1">
          <span v-for="metric in presentation.controlMetrics.slice(0, 3)" :key="metric">
            <MetricLabel :field="metric" :size="11" />
          </span>
          <span v-if="presentation.controlMetrics.length > 3">
            {{
              t('impactAnalysis.scope.moreCount', {
                count: presentation.controlMetrics.length - 3,
              })
            }}
          </span>
        </div>
      </div>
      <div
        v-if="presentation.filters.length"
        class="analysis-scope__row is-top flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.filters') }}</span>
        <div
          class="analysis-scope__filters flex min-w-0 flex-wrap justify-end gap-1 flex-col items-end"
        >
          <span v-for="chip in presentation.filters" :key="`${chip.field}:${chip.operator}`">
            <MetricLabel :field="chip.field" :size="11" />
            {{ chip.operator }}
            <strong>{{ format.formatList(chip.values) }}</strong>
          </span>
        </div>
      </div>
      <div
        v-if="presentation.maxCredits !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.maxCredits') }}</span>
        <strong>{{ format.formatInteger(presentation.maxCredits) }}</strong>
      </div>
      <div
        v-if="presentation.minClicks !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.minClicks') }}</span>
        <strong>{{ format.formatInteger(presentation.minClicks) }}</strong>
      </div>
      <div
        v-if="presentation.preDays !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.daysBefore') }}</span>
        <strong>{{ t('impactAnalysis.scope.daysValue', { count: presentation.preDays }) }}</strong>
      </div>
      <div
        v-if="presentation.postDays !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.daysAfter') }}</span>
        <strong>{{ t('impactAnalysis.scope.daysValue', { count: presentation.postDays }) }}</strong>
      </div>
      <div
        v-if="presentation.minDaysSinceChange !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.minDaysSinceChange') }}</span>
        <strong>{{ format.formatInteger(presentation.minDaysSinceChange) }}</strong>
      </div>
      <div
        v-if="presentation.minActiveDaysBefore !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.minActiveDaysBefore') }}</span>
        <strong>{{ format.formatInteger(presentation.minActiveDaysBefore) }}</strong>
      </div>
      <div
        v-if="presentation.minActiveDaysAfter !== null"
        class="analysis-scope__row flex items-center justify-between gap-md text-[0.75rem]"
      >
        <span>{{ t('impactAnalysis.scope.minActiveDaysAfter') }}</span>
        <strong>{{ format.formatInteger(presentation.minActiveDaysAfter) }}</strong>
      </div>
    </div>
  </Popover>
</template>

<style scoped>
.analysis-scope__row > span:first-child {
  flex: none;
  color: var(--ui-color-muted);
}
.analysis-scope__row > strong {
  min-width: 0;
  text-align: right;
}
.analysis-scope__row > :deep(.metric-label) {
  min-width: 0;
  text-align: right;
}
.analysis-scope__chips > span {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 0.25rem;
  padding-block: 0.125rem;
  padding-inline: 0.5rem;
  border: 1px solid var(--ui-color-border-subtle);
  border-radius: var(--ui-radius-sm);
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
}
.analysis-scope__filters > span {
  display: inline-flex;
  max-width: 100%;
  align-items: center;
  gap: 0.25rem;
  padding-block: 0.125rem;
  padding-inline: 0.5rem;
  border: 1px solid var(--ui-color-border-subtle);
  border-radius: var(--ui-radius-sm);
  background-color: var(--ui-color-surface-sunken);
  color: var(--ui-color-muted);
  font-size: 0.6875rem;
}

.analysis-scope__row.is-top {
  align-items: flex-start;
}
</style>
